import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { legalTermsApi, type TermsStatus } from "../api/legal-terms.api";
import { importError } from "../api/candidate-imports.api";
import { Button, Callout, Card } from "./charte";

/**
 * Nouvelles conditions à accepter avant d’utiliser le service : CGU pour chacun, accord de
 * traitement des données pour le gérant, au nom de l’auto-école. Plein écran, sans contournement.
 */
export function TermsGate({
  status,
  schoolName,
  onLogout,
}: {
  status: TermsStatus;
  schoolName?: string;
  onLogout: () => void;
}) {
  const queryClient = useQueryClient();
  const [cgu, setCgu] = useState(status.cguAccepted);
  const [dpa, setDpa] = useState(false);
  const accept = useMutation({
    mutationFn: () =>
      legalTermsApi.accept({ version: status.version, cgu: true, dpa: status.dpaRequired && dpa }),
    // Toutes les lectures de l’état (école et compte actifs) reçoivent la réponse.
    onSuccess: (next) => queryClient.setQueriesData({ queryKey: ["legal-terms"] }, next),
  });
  const ready = cgu && (!status.dpaRequired || dpa);
  const date = new Date(status.version).toLocaleDateString("fr-FR");
  return (
    <div className="charte-root flex min-h-screen items-center justify-center bg-surface-light p-6">
      <Card className="w-full max-w-xl">
        <h1 className="text-title-sm text-heading">Nos conditions évoluent</h1>
        <p className="mt-3 text-sm text-secondary">
          Pour continuer à utiliser Permio, prenez connaissance des conditions en vigueur au {date} et acceptez-les.
        </p>
        <ul className="mt-4 list-disc pl-5 text-sm">
          <li>
            un <strong>accord de traitement des données</strong> (article 28 du RGPD) précise les engagements de Permio
            pour les données de vos élèves et de votre équipe : sous-traitants, transferts, sécurité, restitution et
            suppression en fin de contrat ;
          </li>
          <li>
            les CGU y renvoient et décrivent l’acceptation des nouvelles versions à la connexion ;
          </li>
          <li>
            la politique de confidentialité précise l’usage de Permio AI pour la reprise des données d’un ancien
            logiciel et les durées de conservation.
          </li>
        </ul>
        <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <a className="text-primary underline" href="/cgu" target="_blank" rel="noreferrer">
            Conditions générales d’utilisation
          </a>
          <a className="text-primary underline" href="/dpa" target="_blank" rel="noreferrer">
            Accord de traitement des données
          </a>
          <a className="text-primary underline" href="/privacy" target="_blank" rel="noreferrer">
            Politique de confidentialité
          </a>
        </p>
        {accept.isError && (
          <div role="alert" className="mt-4">
            <Callout tone="warn">{importError(accept.error)}</Callout>
          </div>
        )}
        <fieldset disabled={accept.isPending} className="mt-5 flex flex-col gap-3 text-sm">
          {!status.cguAccepted && (
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={cgu} onChange={(e) => setCgu(e.target.checked)} />
              <span>J’ai lu et j’accepte les conditions générales d’utilisation.</span>
            </label>
          )}
          {status.dpaRequired && (
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={dpa} onChange={(e) => setDpa(e.target.checked)} />
              <span>
                En qualité de gérant, j’accepte l’accord de traitement des données personnelles au nom de
                {schoolName ? ` l’auto-école ${schoolName}` : " mon auto-école"}.
              </span>
            </label>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="primary" disabled={!ready} onClick={() => accept.mutate()}>
              Accepter et continuer
            </Button>
            <Button variant="ghost" onClick={onLogout}>
              Se déconnecter
            </Button>
          </div>
        </fieldset>
        <p className="mt-4 text-xs text-secondary">
          Une question avant d’accepter ? Écrivez à{" "}
          <a className="underline" href="mailto:contact@permio.fr">
            contact@permio.fr
          </a>
          .
        </p>
      </Card>
    </div>
  );
}
